import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import RiskloOrb from './RiskloOrb';
import { useBrandIdentity } from '../../utils/brandIdentity';
import './askRisklo.css';

/*
 * RiskLo AI launcher: a tab docked to the right edge of the screen. It slides
 * out on hover, peeks now and then with a tip for the current page, can be
 * dragged up and down the edge, and opens the chat panel (also ⌘/Ctrl + J).
 * Mounted once in App.
 */

const AskRiskloPanel = lazy(() => import('./AskRiskloPanel'));

/** Open the panel with a question typed in (the user still presses send). */
export const ASK_AI_EVENT = 'risklo:ask-ai';
export function askRiskloAi(text) {
  window.dispatchEvent(new CustomEvent(ASK_AI_EVENT, { detail: { text: String(text || '') } }));
}

const PAGE_HINTS = {
  dashboard: 'Want a quick read on how your accounts did this month?',
  'client-reporting': 'I can find your biggest drawdown or best weekday.',
  results: 'Ask me which strategy held up best on its worst days.',
  'portfolio-simulator': 'Curious what “Simulate together” really does?',
  'blowout-risk': 'Not sure what NO GO means? Ask me.',
  'rule-risk': 'I can walk through your prop firm’s current consistency rule.',
  'hedging-risk': 'I can explain why two strategies might cancel out.',
  'prop-firm-planner': 'Ask me which strategy and size suit your prop firm plan.',
  'trade-watcher': 'Questions about naked positions? I’ve got you.',
  'position-sizer': 'Tell me your stop and I’ll walk through the sizing math.',
  'concentration-risk': 'What does “acts like N positions” mean? Ask away.',
  'drawdown-risk': 'Want 95% VaR explained in plain English?',
  'stop-planner': 'I can add up what you’d lose if every stop hit.',
  rebalance: 'Buy-only rebalancing, explained in 30 seconds.',
  'consistency-rules': 'Ask me how a firm’s consistency rule hits your payout.',
};
const ROTATING = ['Explain my numbers', 'What’s on the calendar', 'Prop firm rules, verified', 'Market news & facts', 'How any RiskLo tool works'];

const POS_KEY = 'risklo.askRisklo.dockY';
const PEEK_KEY = 'risklo.askRisklo.peeksOff';
const readNum = (k, d) => { try { const v = Number(window.localStorage.getItem(k)); return Number.isFinite(v) && v > 0 ? v : d; } catch (_e) { return d; } };
const store = (k, v) => { try { window.localStorage.setItem(k, String(v)); } catch (_e) { /* private mode */ } };
const peeksOff = () => { try { return window.sessionStorage.getItem(PEEK_KEY) === '1'; } catch (_e) { return false; } };
const isMac = () => typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');

export default function AskRiskloLauncher({ user, currentPage, onOpenPage, locked = false, lockedReason = 'pro', onUpgrade }) {
  const identity = useBrandIdentity();
  // Only white-label brands name the assistant after themselves; everyone else
  // (Partner included) keeps "RiskLo AI".
  const aiName = identity.plan === 'whitelabel' ? `${identity.appName} AI` : 'RiskLo AI';
  const [state, setState] = useState('closed'); // closed | open | closing
  const [dockY, setDockY] = useState(() => readNum(POS_KEY, 68)); // % of viewport height
  const [peek, setPeek] = useState('');
  const [rot, setRot] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [hover, setHover] = useState(false);
  const drag = useRef(null);
  const peeks = useRef(0);

  const open = useCallback(() => { setPeek(''); setState('open'); }, []);
  const [draft, setDraft] = useState('');
  useEffect(() => {
    const onAsk = (e) => { setDraft(e.detail?.text || ''); open(); };
    window.addEventListener(ASK_AI_EVENT, onAsk);
    return () => window.removeEventListener(ASK_AI_EVENT, onAsk);
  }, [open]);
  const close = useCallback(() => {
    setState('closing');
    setTimeout(() => setState('closed'), 230);
  }, []);

  // ⌘/Ctrl + J toggles the panel.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && !e.shiftKey && !e.altKey && String(e.key).toLowerCase() === 'j') {
        e.preventDefault();
        if (state === 'open') close(); else if (state === 'closed') open();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, open, close]);

  // Rotating capability line while the tab is out.
  useEffect(() => {
    if (!hover) return undefined;
    const t = setInterval(() => setRot((r) => (r + 1) % ROTATING.length), 2200);
    return () => clearInterval(t);
  }, [hover]);

  // Peek with a page tip a little after a page change (at most 3 per session).
  useEffect(() => {
    if (locked || state !== 'closed' || peeksOff() || peeks.current >= 3) return undefined;
    const hint = PAGE_HINTS[currentPage];
    if (!hint) return undefined;
    const show = setTimeout(() => { peeks.current += 1; setPeek(hint); }, peeks.current === 0 ? 6000 : 2500);
    const hide = setTimeout(() => setPeek(''), (peeks.current === 0 ? 6000 : 2500) + 7000);
    return () => { clearTimeout(show); clearTimeout(hide); };
  }, [currentPage, state, locked]);

  const stopPeeks = (e) => {
    e.stopPropagation();
    setPeek('');
    try { window.sessionStorage.setItem(PEEK_KEY, '1'); } catch (_e) { /* private mode */ }
  };

  // Drag along the edge; a press that barely moves is a click.
  const onPointerDown = (e) => {
    if (e.button !== 0) return;
    drag.current = { startY: e.clientY, startDock: dockY, moved: false };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d) return;
    const dy = e.clientY - d.startY;
    if (!d.moved && Math.abs(dy) < 6) return;
    d.moved = true;
    setDragging(true);
    const pct = d.startDock + (dy / window.innerHeight) * 100;
    setDockY(Math.max(14, Math.min(86, pct)));
  };
  const onPointerUp = () => {
    const d = drag.current;
    drag.current = null;
    if (!d) return;
    if (d.moved) {
      setDragging(false);
      setDockY((y) => { store(POS_KEY, y.toFixed(1)); return y; });
    } else {
      open();
    }
  };

  const shortcut = isMac() ? '⌘J' : 'Ctrl+J';
  const firstName = String(user?.name || '').trim().split(/\s+/)[0];

  return (
    <>
      {state === 'closed' ? (
        <div
          className={`ask-dock${hover || peek ? ' is-out' : ''}${dragging ? ' is-dragging' : ''}`}
          style={{ top: `${dockY}%` }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
        >
          {peek ? (
            <div className="ask-peek" role="status">
              <span>{firstName ? `${firstName}, ${/^I[ ’']/.test(peek) ? peek : peek.charAt(0).toLowerCase() + peek.slice(1)}` : peek}</span>
              <button type="button" className="ask-peek-x" onClick={stopPeeks} aria-label="Stop tips">×</button>
            </div>
          ) : null}
          <button
            type="button"
            className="ask-launcher"
            aria-label={locked ? `Ask ${aiName} (Pro)` : `Ask ${aiName}`}
            title={`Ask ${aiName} (${shortcut}) · drag to move`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={() => { drag.current = null; setDragging(false); }}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); } }}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
          >
            <span className="ask-launcher-orb">
              <RiskloOrb size={38} track />
              {locked ? <span className="ask-launcher-lock" aria-hidden="true"><svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="11" width="14" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></svg></span> : null}
            </span>
            <span className="ask-launcher-more">
              <span className="ask-launcher-text">
                <span className="ask-launcher-label">Ask {aiName}{locked ? <span className="ask-launcher-pro">PRO</span> : null}</span>
                <span className="ask-launcher-sub" key={rot}>{ROTATING[rot]}</span>
              </span>
              <span className="ask-launcher-kbd">{shortcut}</span>
            </span>
            <span className="ask-launcher-grip" aria-hidden="true" />
          </button>
        </div>
      ) : (
        <Suspense fallback={null}>
          <AskRiskloPanel
            user={user}
            currentPage={currentPage}
            closing={state === 'closing'}
            locked={locked}
            lockedReason={lockedReason}
            onUpgrade={() => { close(); onUpgrade?.(); }}
            onOpenPage={(page) => { onOpenPage(page); if (window.matchMedia?.('(max-width: 720px)')?.matches) close(); }}
            onClose={close}
            draft={draft}
            onDraftUsed={() => setDraft('')}
          />
        </Suspense>
      )}
    </>
  );
}
